
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MockAPI } from '../services/api';
import { Farm } from '../types';
import { Button } from '../components/ui';
import { Save } from 'lucide-react';
import { useFarm } from '../services/farmContext';

interface FarmFormProps {
    farmId?: string;
    onClose?: () => void;
    onSuccess?: () => void;
}

const FarmForm: React.FC<FarmFormProps> = ({ farmId: propFarmId, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { activeFarm, setActiveFarmId } = useFarm();
  
  // Use prop ID if available, otherwise URL ID
  const activeId = propFarmId || id;
  const isEditMode = Boolean(activeId);

  const [formData, setFormData] = useState<Partial<Farm>>({
    name: '',
    address: '',
    llc_name: '',
    usda_farm_number: '',
    county: '',
    state: '',
    notes: ''
  });
  
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (isEditMode && activeId) {
        const farm = await MockAPI.getFarm(activeId);
        if (farm) {
          setFormData(farm);
        } else {
          setError("Farm not found");
        }
      } else {
        // Prevent creating additional farms when one already exists
        const existing = activeFarm || await MockAPI.getSingletonFarm();
        if (existing) {
          setError("A farm already exists. Editing existing farm instead.");
          if (onSuccess) {
            onSuccess();
          } else {
            navigate(`/farms/${existing.farm_id}/edit`);
          }
        }
      }
    };
    load();
  }, [isEditMode, activeId, activeFarm, navigate, onSuccess]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      setError("Farm name is required");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const saved = await MockAPI.saveFarm(formData as Farm);
      await setActiveFarmId(saved.farm_id);
      
      if (onSuccess) onSuccess();
      else navigate('/');

    } catch (err: any) {
      setError(err.message || "Failed to save farm");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {isEditMode ? 'Edit Farm' : 'Add New Farm'}
          </h1>
        </div>
        {onClose && (
            <Button variant="outline" onClick={onClose} className="p-1 px-3 text-xs">Close</Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Farm Name *</label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">LLC Name</label>
              <input
                type="text"
                name="llc_name"
                value={formData.llc_name || ''}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address || ''}
                onChange={handleChange}
                placeholder="123 Country Road"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">USDA Farm #</label>
                  <input
                    type="text"
                    name="usda_farm_number"
                    value={formData.usda_farm_number || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">County</label>
                  <input
                    type="text"
                    name="county"
                    value={formData.county || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                  />
                </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">State</label>
              <input
                type="text"
                name="state"
                value={formData.state || ''}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                name="notes"
                rows={2}
                value={formData.notes || ''}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
              />
            </div>
        </div>

        <div className="pt-4 mt-4 border-t border-gray-100 flex justify-end">
             <Button type="submit" disabled={isProcessing}>
               <Save className="h-4 w-4 mr-2" /> {isProcessing ? 'Processing...' : 'Save'}
             </Button>
        </div>
      </form>
    </div>
  );
};

export default FarmForm;
